import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Eye,
  Mail,
  Cookie,
  ChevronRight,
  Download,
  Printer,
  ExternalLink,
  Info,
  CheckCircle2,
  AlertCircle,
  Scale,
  Globe,
  Users,
} from "lucide-react";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("intro");
  const [scrolled, setScrolled] = useState(false);
  const downloadButtonRef = useRef(null);
  const printButtonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    {
      id: "intro",
      icon: <Info className="w-5 h-5" />,
      title: "Introduction",
      content:
        "At Trade Bridge, we are committed to protecting your privacy and building trust through transparency. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our premium wholesale marketplace platform.",
    },
    {
      id: "collection",
      icon: <Shield className="w-5 h-5" />,
      title: "Information We Collect",
      content:
        "As a B2B wholesale platform connecting global buyers with trusted suppliers, we collect the following types of information:",
      list: [
        "Business account details (Company name, Tax ID, Commercial registration)",
        "Contact information (Name, Email, Phone, Business address)",
        "Trading data (Inquiry history, orders, shipping preferences)",
        "Verification documents (Business license, VAT certificate, etc.)",
        "Usage data and analytics to improve platform experience",
      ],
    },
    {
      id: "usage",
      icon: <Lock className="w-5 h-5" />,
      title: "How We Use Your Information",
      content:
        "We use your information to deliver a secure and efficient wholesale experience:",
      list: [
        "Facilitating buyer-supplier connections and order processing",
        "Verifying business legitimacy and preventing fraudulent activity",
        "Providing personalized sourcing recommendations",
        "Managing logistics, shipping options, and export documentation",
        "Communicating important updates, support, and market insights",
      ],
    },
    {
      id: "sharing",
      icon: <Eye className="w-5 h-5" />,
      title: "Information Sharing",
      content:
        "We do not sell your data. We only share information when necessary to provide our services:",
      list: [
        "With trusted logistics and shipping partners for order fulfillment",
        "With payment processors and financial institutions",
        "With legal authorities when required by Egyptian or international law",
        "With service providers under strict data processing agreements",
      ],
    },
    {
      id: "cookies",
      icon: <Cookie className="w-5 h-5" />,
      title: "Cookies and Tracking Technologies",
      content:
        "We use essential cookies and similar technologies to ensure the proper functioning of our platform, remember your preferences, and analyze how our wholesale tools are used. You can manage your cookie preferences through your browser settings.",
    },
    {
      id: "rights",
      icon: <Scale className="w-5 h-5" />,
      title: "Your Rights and Choices",
      content:
        "You have the right to control your personal and business data. Depending on your location and applicable laws (including Egyptian Data Protection Law), you may:",
      list: [
        "Access, correct, or update your information",
        "Request deletion of your account and associated data",
        "Object to certain processing activities",
        "Export your data in a portable format",
        "Withdraw consent where processing is based on consent",
      ],
    },
    {
      id: "contact",
      icon: <Mail className="w-5 h-5" />,
      title: "Contact Our Privacy Team",
      content:
        "If you have any questions about this Privacy Policy or how we handle your data, please contact our dedicated privacy officer:",
      contactInfo: {
        email: "admin@tradebridge-export.com",
        phone: "+20 111 477 9577",
        address: "Trade Bridge, Alexandria, Egypt",
        response: "We typically respond within 2 business days",
      },
    },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  const generatePDFContent = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Trade Bridge Privacy Policy</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 30px;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 2px solid #3b82f6;
          }
          h1 {
            font-size: 32px;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 10px;
          }
          .date {
            color: #666;
            font-size: 14px;
            margin-top: 10px;
          }
          .section {
            margin-bottom: 40px;
            page-break-inside: avoid;
          }
          .section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
          }
          .section-icon {
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #3b82f6, #0ea5e9);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 20px;
          }
          h2 {
            font-size: 24px;
            font-weight: 600;
            color: #1e40af;
          }
          .section-content {
            color: #4b5563;
            font-size: 15px;
            margin-bottom: 16px;
            line-height: 1.7;
          }
          .list {
            list-style: none;
            margin-top: 16px;
          }
          .list-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 12px;
            padding: 12px;
            background: #f9fafb;
            border-radius: 12px;
          }
          .check-icon {
            color: #10b981;
            font-weight: bold;
            font-size: 18px;
            flex-shrink: 0;
          }
          .contact-box {
            background: linear-gradient(135deg, #1f2937, #111827);
            color: white;
            padding: 24px;
            border-radius: 20px;
            margin-top: 24px;
          }
          .contact-item {
            margin-bottom: 12px;
          }
          .contact-label {
            font-weight: 600;
            margin-bottom: 4px;
          }
          .footer {
            margin-top: 60px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            font-size: 12px;
            color: #6b7280;
          }
          @media print {
            body {
              padding: 20px;
            }
            .section {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Trade Bridge Privacy Policy</h1>
          <div class="date">Last Updated: April 2026 | Version 1.2.0</div>
        </div>
        
        ${sections
          .map(
            (section) => `
          <div class="section">
            <div class="section-header">
              <div class="section-icon">
                ${
                  section.title === "Introduction"
                    ? "ℹ️"
                    : section.title === "Information We Collect"
                      ? "🛡️"
                      : section.title === "How We Use Your Information"
                        ? "🔒"
                        : section.title === "Information Sharing"
                          ? "👁️"
                          : section.title ===
                              "Cookies and Tracking Technologies"
                            ? "🍪"
                            : section.title === "Your Rights and Choices"
                              ? "⚖️"
                              : "✉️"
                }
              </div>
              <h2>${section.title}</h2>
            </div>
            <div class="section-content">
              ${section.content}
            </div>
            ${
              section.list
                ? `
              <div class="list">
                ${section.list
                  .map(
                    (item) => `
                  <div class="list-item">
                    <span class="check-icon">✓</span>
                    <span>${item}</span>
                  </div>
                `,
                  )
                  .join("")}
              </div>
            `
                : ""
            }
            ${
              section.contactInfo
                ? `
              <div class="contact-box">
                <div class="contact-item">
                  <div class="contact-label">Email:</div>
                  <div>${section.contactInfo.email}</div>
                </div>
                <div class="contact-item">
                  <div class="contact-label">Phone:</div>
                  <div>${section.contactInfo.phone}</div>
                </div>
                <div class="contact-item">
                  <div class="contact-label">Address:</div>
                  <div>${section.contactInfo.address}</div>
                </div>
                <div class="contact-item">
                  <div class="contact-label">Response Time:</div>
                  <div>${section.contactInfo.response}</div>
                </div>
              </div>
            `
                : ""
            }
          </div>
        `,
          )
          .join("")}
        
        <div class="footer">
          <p>This Privacy Policy complies with Egyptian data protection regulations and international best practices.</p>
          <p>By using Trade Bridge, you consent to the practices described herein.</p>
          <p style="margin-top: 10px;">© ${new Date().getFullYear()} Trade Bridge. All rights reserved.</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPDF = async () => {
    const button = downloadButtonRef.current;
    let originalText = "";

    try {
      if (button) {
        originalText = button.innerHTML;
        button.innerHTML = `
          <svg class="animate-spin w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Generating PDF...
        `;
        button.disabled = true;
      }

      const fileName = `Trade_Bridge_Privacy_Policy_${new Date().getFullYear()}.pdf`;
      const htmlContent = generatePDFContent();

      // Create a blob with HTML content
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      // Reset button
      setTimeout(() => {
        if (button) {
          button.innerHTML = originalText;
          button.disabled = false;
        }
      }, 1200);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("Sorry, PDF download failed. Please try again or contact support.");

      if (button) {
        button.innerHTML = originalText;
        button.disabled = false;
      }
    }
  };

  const handlePrint = () => {
    const printContent = generatePDFContent();
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[30%] -left-[15%] w-[70%] h-[70%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] lg:min-h-[75vh] flex items-center justify-center pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 mb-6">
                <Shield className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 tracking-wide">
                  TRUST &amp; TRANSPARENCY
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                <span className="text-gray-900 dark:text-white">Privacy</span>{" "}
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Policy
                </span>
              </h1>

              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
                Last updated: April 2026 • Version 1.2.0
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  ref={downloadButtonRef}
                  onClick={handleDownloadPDF}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
                >
                  <Download className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                  Download PDF
                </button>
                <button
                  ref={printButtonRef}
                  onClick={handlePrint}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 font-medium rounded-2xl border border-white/50 dark:border-gray-800/50 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all"
                >
                  <Printer className="w-4 h-4" />
                  Print Policy
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-80 shrink-0">
            <div className="sticky top-24 space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 px-4">
                TABLE OF CONTENTS
              </p>

              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl text-sm font-medium transition-all ${
                    activeSection === section.id
                      ? "bg-white dark:bg-gray-800 shadow-md text-primary-600 dark:text-primary-400"
                      : "text-gray-600 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-900/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <ChevronRight
                    size={18}
                    className={`transition-transform ${activeSection === section.id ? "rotate-90" : ""}`}
                  />
                </button>
              ))}

              {/* Help Card */}
              <div className="mt-10 p-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl text-white shadow-xl">
                <Users className="w-8 h-8 mb-3 opacity-90" />
                <h4 className="font-semibold mb-1">
                  Questions about your data?
                </h4>
                <p className="text-sm text-white/80 mb-4">
                  Our privacy team is ready to assist you with any concerns.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-medium bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all"
                >
                  Contact Privacy Officer
                  <ExternalLink size={14} />
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            <div className="space-y-16">
              {sections.map((section) => (
                <motion.section
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="scroll-mt-24"
                >
                  <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden">
                    <div className="p-8 md:p-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white flex-shrink-0">
                          {section.icon}
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                          {section.title}
                        </h2>
                      </div>

                      <div className="prose prose-gray dark:prose-invert max-w-none text-[15px] leading-relaxed">
                        <p className="text-gray-700 dark:text-gray-300">
                          {section.content}
                        </p>

                        {section.list && (
                          <ul className="mt-6 space-y-3">
                            {section.list.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-2xl p-4 border border-white/40 dark:border-gray-700"
                              >
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {section.contactInfo && (
                          <div className="mt-10 p-8 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-black rounded-3xl text-white">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="w-11 h-11 bg-white/10 rounded-2xl flex items-center justify-center">
                                <Mail className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium text-lg">
                                  {section.contactInfo.email}
                                </p>
                                <p className="text-sm text-white/70">
                                  {section.contactInfo.phone}
                                </p>
                              </div>
                            </div>
                            <p className="text-white/70 text-sm mb-4">
                              {section.contactInfo.address}
                            </p>
                            <div className="inline-flex items-center gap-2 text-xs bg-white/10 px-4 py-2 rounded-xl">
                              <AlertCircle size={14} />
                              <span>{section.contactInfo.response}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.section>
              ))}
            </div>

            {/* Final Note */}
            <div className="mt-20 pt-10 border-t border-gray-200 dark:border-gray-800 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                This Privacy Policy complies with Egyptian data protection
                regulations and international best practices. By using Trade
                Bridge, you consent to the practices described herein.
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
